import { Button, Card, Checkbox, Col, Input, Progress, Row, Space, Tag, Tooltip, Typography } from "antd";
import { UploadFile } from "antd/lib/upload/interface";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../../../assets/icons/icon";
import CardBox from "../../../../components/cardBox";
import DragUpload from "../../../../components/dragUpload";
import DividerLine from "../../../../components/lineSplit";
import LoadingContainer from "../../../../components/loadingContainer";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import PopMaybeOver from "../../../../components/popMaybeOver";
import UploadButton from "../../../../components/uploadButton";
import TaxCaseStore from "../../../../contexts/taxCaseStore";
import TaxFlowStore from "../../../../contexts/taxFlowStore";
import useIsDraggingFiles from "../../../../hooks/useIsDraggingFiles";
import _t from "../../../../lang/translate";
import { openFileLink } from "../../../../services/mediaService";
import { deleteTaxScenarioFile, generateDocumentation, taxScenarioFileUrl } from "../../../../services/taxScenarioService";
import { ApiFile } from "../../../../types/appTypes";
import { accepted } from "../../../../utilities/acceptedFiles";
import { convertToUploadFile, convertToUploadFileList, getFileId } from "../../../../utilities/convertTypes";
import format from "../../../../utilities/formatNumbers";
import { capitalize } from "../../../../utilities/stringExtraFunctions";



type ScenarioFile = {
  file: UploadFile;
  tag: {
    id: string;
    label: string;
    color: string;
  };
  canDelete: boolean;
  canDownload: boolean;
  selected: boolean;
}
export default function TaxFlowDocumentation() {
  const { taxCase } = TaxCaseStore.useContainer()
  const { taxFlow, taxScenario } = TaxFlowStore.useContainer()
  const [selectedFiles, setSelectedFiles] = useState<(string | number)[]>([])
  const [scenarioFiles, setScenarioFiles] = useState<UploadFile[]>(convertToUploadFileList(taxScenario?.files ?? []))
  const didMountedRef = useRef(false)
  useEffect(() => {
    if (!didMountedRef.current) {
      didMountedRef.current = true;
      return;
    }
    if (taxScenario?.files?.length) {
      setScenarioFiles(convertToUploadFileList(taxScenario.files))
    }
  }, [taxScenario?.files])

  const motorAtlasDocuments = useMemo(() => {
    const selected = taxFlow.newPrice?.manager?.data.selectedNewPrice;
    const newPrices = taxFlow.newPrice?.manager?.data.newPrices;
    const np = newPrices?.find(item => item.id === selected)
    return np?.documents;
  }, [taxFlow.newPrice?.manager?.data.selectedNewPrice, taxFlow.newPrice?.manager?.data.newPrices])

  const files = useMemo(() => {
    const files: ScenarioFile[] = [];

    scenarioFiles.forEach(file => {
      files.push({
        file: file,
        canDelete: true,
        canDownload: true,
        tag: { id: "scenario", label: "Denne version", color: "#1EC670" },
        selected: selectedFiles.includes(getFileId(file)),
      })
    })

    taxCase.files?.forEach(file => {
      files.push({
        file,
        canDelete: false,
        canDownload: false,
        tag: { id: "case", label: "Sag (Fælles)", color: "#95A5AE" },
        selected: selectedFiles.includes(getFileId(file))
      })
    })

    motorAtlasDocuments?.forEach(doc => {
      files.push({
        file: {
          uid: doc.id,
          name: doc.name,
        },
        canDelete: false,
        canDownload: false,
        tag: { id: "motor_atlas", label: "Motor Atlas", color: "#1677FF" },
        selected: selectedFiles.includes(doc.id)
      })
    })

    return files
  }, [taxCase.files, scenarioFiles, selectedFiles, motorAtlasDocuments])

  function toggleAllChecked(allChecked: boolean) {
    if (allChecked) {
      setSelectedFiles(files.map(item => getFileId(item.file)))
    } else {
      setSelectedFiles([])
    }
  }
  return (
    <div className="p-1">
      <Row gutter={[16, 16]} style={{ flexWrap: "wrap-reverse" }}>
        <Col flex={"1 1 600px"}>
          <div className="p-1 border-light border-radius">
            <h3>Vælg dokumenter til PDF</h3>
            <CardBox className="mt-1 mb-1 border-light">
              Markér de dokumenter du ønsker medtaget i den samlede dokumentationspakke. Du kan uploade ekstra vedhæftninger herunder, som kun lever på versionen.
            </CardBox>
            <Card title={
              <Row justify="space-between">
                <Col>{_t("documents")}</Col>
                <Col>
                  <Checkbox onChange={e => toggleAllChecked(e.target.checked)} checked={selectedFiles.length === files.length}>Marker alle ({files.length})</Checkbox>
                </Col>
              </Row>
            } >
              <SelectDocuments scenarioFiles={scenarioFiles} setScenarioFiles={setScenarioFiles} setSelectedFiles={setSelectedFiles} files={files} />
            </Card>
          </div>
        </Col>
        <Col flex={"1 1 300px"}>
          <DocumentationActions files={files} selectedFiles={selectedFiles} />
        </Col>
      </Row>
    </div>
  )
}


type DocumentationActionsProps = {
  files: ScenarioFile[];
  selectedFiles: (string | number)[];
}
function DocumentationActions({ files, selectedFiles }: DocumentationActionsProps) {
  const { taxFlow, setTaxFlow, dirty, taxScenario } = TaxFlowStore.useContainer()
  const { taxCase, setTaxCase } = TaxCaseStore.useContainer()
  const [loading, setLoading] = useState(false)

  const externalNote = taxFlow.externalNote
  function onExternalNoteChange(val: string) {
    setTaxFlow((prev) => ({
      ...prev,
      externalNote: val
    }))
  }

  const internalNote = taxFlow.internalNote
  function onInternalNoteChange(val: string) {
    setTaxFlow((prev) => ({
      ...prev,
      internalNote: val
    }))
  }

  async function generateCollectivePDF() {
    try {
      if (!taxScenario?.id) return;
      setLoading(true)

      const filesToGenerate = files.filter(item => selectedFiles.includes(getFileId(item.file)))
      const { data: newTaxScenario } = await generateDocumentation(taxCase.id, taxScenario?.id, {
        files: filesToGenerate?.filter(item => item.tag.id !== 'motor_atlas')?.map(item => ({ id: getFileId(item.file) })) ?? [],
        motorAtlasFiles: filesToGenerate?.filter(item => item.tag.id === 'motor_atlas')?.map(item => ({ id: getFileId(item.file) as string })) ?? [],
      })
      const url = newTaxScenario?.documentation?.url
      setTaxCase({
        ...taxCase,
        scenarios: {
          ...taxCase.scenarios,
          offer: newTaxScenario
        }
      })
      if (url) openFileLink(url)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false)
    }
  }
  async function generateOnlyDocumentation() {
    try {
      if (!taxScenario?.id) return;
      setLoading(true)
      const { data: newTaxScenario } = await generateDocumentation(taxCase.id, taxScenario.id, {
        files: [],
        motorAtlasFiles: []
      })
      const url = newTaxScenario?.documentation?.url
      setTaxCase({
        ...taxCase,
        scenarios: {
          ...taxCase.scenarios,
          offer: newTaxScenario
        }
      })
      if (url) openFileLink(url)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="border-light border-radius bg-light p-1">
      <h3 className="mb-1">{_t("documentation")}</h3>
      <p className="mt-1 mb-05">{_t("notes", "to_the_case")}</p>
      <Input.TextArea value={externalNote ?? ''} onChange={(e) => onExternalNoteChange(e.target.value)} />

      <p className="mt-1 mb-05">{_t("internal_note_to_documentation")}</p>
      <Input.TextArea value={internalNote ?? ''} onChange={(e) => onInternalNoteChange(e.target.value)} />

      <DividerLine />

      <h4>{_t("document_overview")}</h4>
      <Row justify="space-between">
        <Col>{_t("total")}</Col>
        <Col>{files?.length} {_t("documents")}</Col>
      </Row>
      <Row className="text-bold" justify="space-between">
        <Col>{_t("chosen_for_pdf")}</Col>
        <Col>{selectedFiles?.length} {_t("documents")}</Col>
      </Row>

      <PopMaybeOver title={dirty ? _t("save_before_action") : null}>
        <Button type="primary" icon={<Icon name="download-outline" />}
          className="w-100 mt-1 mb-05"
          style={{ width: "100%" }}
          disabled={dirty || files.every(f => !f.selected)}
          onClick={generateCollectivePDF} loading={loading}
        >
          {_t("generate_collective_pdf")}
        </Button>
      </PopMaybeOver>

      <PopMaybeOver title={dirty ? _t("save_before_action") : null}>
        <Button type="text" onClick={generateOnlyDocumentation} loading={loading}
          disabled={dirty}
          className="w-100"
          style={{ width: "100%" }}
        >
          {_t("generate_only_documentation")}
        </Button>
      </PopMaybeOver>
    </div>

  )
}

type SelectDocumentsProps = {
  files: ScenarioFile[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<(string | number)[]>>;
  scenarioFiles: UploadFile<any>[];
  setScenarioFiles: React.Dispatch<React.SetStateAction<UploadFile<any>[]>>;
}
function SelectDocuments({ files, setSelectedFiles, scenarioFiles, setScenarioFiles }: SelectDocumentsProps) {
  const [isDragging] = useIsDraggingFiles();
  const [loading, setLoading] = useState(false)
  const { taxCase } = TaxCaseStore.useContainer()
  const { taxScenario, dirty } = TaxFlowStore.useContainer()
  const fileUrl = taxScenarioFileUrl(taxCase.id, taxScenario?.id ?? -1)
  const [loadingIds, setLoadingIds] = useState<(string | number)[]>([])
  const uploadDisabled = !taxScenario?.id || dirty;

  const startLoading = (id: number | string) => {
    setLoadingIds((ids) => (!ids.includes(id) ? [...ids, id] : ids));
  };

  const endLoading = (id: number | string) => {
    setLoadingIds((ids) => (ids.includes(id) ? ids.filter((i) => i !== id) : ids));
  };

  const handleDownloadAll = async () => {
    try {
      setLoading(true);
      // const { data } = await downloadAllFiles(taxCase.id);
      // if (data?.url) window.open(data.url, "_blank");
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false);
    }
  };

  const handleFileRemove = async (file: UploadFile) => {
    const serverId = getFileId(file);
    const localId = file.uid;
    let canRemove: boolean | undefined = true;
    if (!taxScenario?.id) return;
    if (serverId) {
      try {
        startLoading(localId)
        await deleteTaxScenarioFile(taxCase.id, taxScenario.id, serverId);
      } catch (error) {
        renderErrorMessage(error)
        canRemove = false;
      } finally {
        endLoading(localId);
      }
    }
    if (canRemove) {
      setScenarioFiles((fileList) => {
        const updatedFileList = fileList?.filter((f) => f.uid !== localId) || [];
        return updatedFileList
      })
    }
  };
  function onSuccess(apiFile: ApiFile, file: UploadFile): void {
    setScenarioFiles((prev) => {
      const index = prev.findIndex(f => f.uid === file.uid)
      if (index === -1) return prev
      return prev.toSpliced(index, 1, convertToUploadFile(apiFile))
    })
  }

  const total = files?.length
  return (
    <>
      {files?.map((item, i) => (
        <div key={getFileId(item.file)}>
          <FileRender
            file={item.file}
            onRemove={item.canDelete ? handleFileRemove : undefined}
            selected={item.selected}
            tag={item.tag}
            loading={loadingIds.includes(getFileId(item.file))}
            canDownload={item.canDownload}
            select={(checked) => {
              setSelectedFiles((prev) => {
                if (checked) {
                  return [...prev, getFileId(item.file)]
                } else {
                  const index = prev.indexOf(getFileId(item.file))
                  if (index > -1) {
                    return [...prev.slice(0, index), ...prev.slice(index + 1)]
                  }
                  return prev
                }
              })
            }}
          />
          {i + 1 < files.length ? (
            <DividerLine className="mt-05 mb-05" />
          ) : (
            <DividerLine weight={2} className="mt-05 mb-1" />
          )}
        </div>
      ))}
      {
        <DragUpload className={`${isDragging && !uploadDisabled ? "is-dragging" : ""} file-drop-zone`} showUploadList={false}
          disabled={uploadDisabled}
          action={fileUrl}
          accept={accepted.join(",")}
          multiple={true}
          fileList={scenarioFiles}
          onChange={(info) => {
            setScenarioFiles(info.fileList)
          }}
          onSuccess={onSuccess}
        >
          <Typography.Title level={3} className="mb-0">
            {_t("msg.drop_her_to_attach")}
          </Typography.Title>
        </DragUpload>
      }
      <Row align="middle">
        <Col flex="auto" className="muted">
          <strong>{total}</strong> {_t("files_in_attached_to_case")}
        </Col>
        <Col>
          <Space>
            <small>Uploadede filer knyttes til denne version</small>
            <UploadButton
              disabled={uploadDisabled}
              action={fileUrl}
              accept={accepted.join(",")}
              multiple={true}
              fileList={scenarioFiles}
              onChange={(info) => {
                setScenarioFiles(info.fileList)
              }}
              onSuccess={onSuccess}
            />
          </Space>
        </Col>
      </Row>
    </>
  )
}



interface UploadFileRenderProps extends React.HTMLAttributes<HTMLDivElement> {
  file: UploadFile;
  onRemove?: (file: UploadFile) => void;
  onAdminToggle?: (file: UploadFile) => void;
  canDownload?: boolean;
  loading?: boolean;
  selected?: boolean;
  select: (checked: boolean) => void;
  tag: {
    color: string;
    label: string;
  }
}

const FileRender: FC<UploadFileRenderProps> = ({
  file,
  onRemove,
  onAdminToggle,
  canDownload,
  loading = false,
  selected,
  select,
  tag,
  ...rest
}) => {

  return (
    <LoadingContainer loading={file.status === "uploading" || loading} {...rest}>
      <Row gutter={8} align="middle" wrap={false}>
        <Col>
          <Checkbox checked={selected} onChange={e => select(e.target.checked)} />
        </Col>
        <Col flex="auto">
          <Typography.Text
            type={file.status === "error" ? "danger" : undefined}
            className="mx-width-340"
            style={{ width: "100%" }}
            title={file.status === "error" ? file?.error?.response?.data?.message : undefined}
            ellipsis
          >
            <Icon name="attach-outline" size="large" className="mr-05" />
            {capitalize(file.name)}
            <small> — {file.size && format.fileSize(file.size)}</small>
          </Typography.Text>
        </Col>
        {file.status === "uploading" && (
          <Col span={8}>
            <Progress type="line" showInfo={false} percent={file.percent} />
          </Col>
        )}

        <Col>
          <Space>
            <Tag style={{
              borderRadius: 20,
              background: `${tag.color}30`,
              color: tag.color,
              border: `1.4px solid ${tag.color}70`
            }}>{tag.label}</Tag>
            {canDownload && (
              <Tooltip title={_t("download")}>
                <Button
                  onClick={() => openFileLink(file.url || file.response?.url)}
                  shape="circle"
                  type="text"
                >
                  <Icon name="download-outline" />
                </Button>
              </Tooltip>
            )}
            {onRemove && (
              <Button onClick={() => onRemove(file)} shape="circle" danger type="text">
                <Icon name="trash-outline" />
              </Button>
            )}
          </Space>
        </Col>
      </Row>
    </LoadingContainer>
  );
};


